import { CATEGORY_ORDER } from "../deviceCategories";
import type { DeviceCategory } from "../deviceCategories";
import type { DevicesInRect, NearestAssignable, PlanRect, SummariseSelection } from "./types";

const overlaps = (box: PlanRect, rect: PlanRect): boolean =>
  box.x1 <= rect.x2 && box.x2 >= rect.x1 && box.y1 <= rect.y2 && box.y2 >= rect.y1;

export const devicesInRect: DevicesInRect = (devices, rect) =>
  devices.filter(({ bbox }) => overlaps(bbox, rect)).map((device) => device.key);

// Inside a symbol picks it, the smallest one when boxes overlap; otherwise the nearest within reach.
export const nearestAssignable: NearestAssignable = (devices, point, radius) => {
  const containing = devices
    .filter(({ bbox }) => point.x >= bbox.x1 && point.x <= bbox.x2 && point.y >= bbox.y1 && point.y <= bbox.y2)
    .sort((a, b) => area(a.bbox) - area(b.bbox));
  if (containing.length) return containing[0].key;
  let best: string | null = null;
  let bestDistance = radius * radius;
  devices.forEach((device) => {
    const distance = (device.position.x - point.x) ** 2 + (device.position.y - point.y) ** 2;
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = device.key;
    }
  });
  return best;
};

const area = (box: PlanRect): number => (box.x2 - box.x1) * (box.y2 - box.y1);

export const summariseSelection: SummariseSelection = (devices, keys) => {
  const wanted = new Set(keys);
  const picked = devices.filter((device) => wanted.has(device.key));
  const counts = new Map<DeviceCategory, number>();
  picked.forEach(({ category }) => {
    if (category !== "other") counts.set(category, (counts.get(category) ?? 0) + 1);
  });
  const categories = [...counts.entries()]
    .sort(([a, countA], [b, countB]) => countB - countA || CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b))
    .map(([category]) => category);
  const positions = picked.map((device) => device.position);
  const centre = positions.length
    ? {
        x: positions.reduce((sum, at) => sum + at.x, 0) / positions.length,
        y: positions.reduce((sum, at) => sum + at.y, 0) / positions.length,
      }
    : null;
  return {
    count: picked.length,
    categories,
    destinationIds: picked.flatMap((device) => (device.kind === "noCircuit" ? [] : [device.destinationId])),
    adoptions: picked.flatMap((device) =>
      device.kind === "noCircuit"
        ? [{ elementId: device.elementId, position: device.position, name: device.name }]
        : [],
    ),
    centre,
    positions,
  };
};
